import { AppButton } from '@/components/design-system/AppButton';
import logger from '@/utils/logger';

import { useEffect, useState } from 'react';

/* eslint-disable qld/no-raw-mantine-primitives -- Alert has no design-system wrapper yet; keep this inline signup error state local until one lands. */
import {
  Alert,
  Anchor,
  Checkbox,
  Container,
  Divider,
  Group,
  Modal,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { defined } from '@teo/types';
import { useLocation, useNavigate } from 'react-router-dom';

import { GoogleAuthButton } from '../components/GoogleAuthButton';
import { useAuth } from '../contexts/AuthContext';
import { storeAuthTokens } from '../lib/authSession';
import { trpc } from '../lib/trpc';
import privacyHtml from '../static/privacy-policy.html?raw';
import termsHtml from '../static/terms-of-use.html?raw';
import {
  validateConfirmPassword,
  validateEmail,
  validateName,
  validatePassword,
  validatePhone,
} from '../utils/validation';
import { getSignupErrorCode, getSignupErrorPresentation } from './signupErrorPresentation';

// User registration form with comprehensive validation
// Intent: Provide secure user registration with proper validation for all required fields
// Ensures data integrity and user experience consistency with login flow
export function SignupPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [inviteCodeModalOpen, setInviteCodeModalOpen] = useState(false);
  const [manualInviteCode, setManualInviteCode] = useState('');
  const [signupErrorMessage, setSignupErrorMessage] = useState<string | null>(null);

  const { setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract invite code from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const inviteCodeFromUrl = searchParams.get('inviteCode')?.trim().toUpperCase() ?? '';
  logger.log({ inviteCodeFromUrl }, 'Rendering SignupPage Invite code from URL:');

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      firstName: searchParams.get('firstName') || '',
      lastName: searchParams.get('lastName') || '',
      email: '',
      passwords: {
        first: '',
        second: '',
      },
      phone: '',
      inviteCode: inviteCodeFromUrl,
      termsAccepted: true,
      privacyAccepted: true,
    },
    validate: {
      firstName: (value) => {
        const result = validateName(value, 'First name');
        return result.success ? null : result.error;
      },
      lastName: (value) => {
        const result = validateName(value, 'Last name');
        return result.success ? null : result.error;
      },
      email: (value) => {
        const result = validateEmail(value);
        return result.success ? null : result.error;
      },
      passwords: {
        // LR: This is not working
        // [formRootRule]: (value, _, path) => {
        //   return value.first === value.second
        //     ? 'Passwords do not matchzzzz'
        //     : null
        // },
        first: (value) => {
          const result = validatePassword(value);
          return result.success ? null : result.error;
        },
        second: (value, values) => {
          const result = validateConfirmPassword(values.passwords.first, value);
          return result.success ? null : result.error;
        },
      },
      phone: (value) => {
        if (!value) return null; // Phone is optional
        const result = validatePhone(value);
        return result.success ? null : result.error;
      },
      termsAccepted: (value) => {
        return value ? null : 'You must accept the Terms of Use';
      },
      privacyAccepted: (value) => {
        return value ? null : 'You must accept the Privacy Policy';
      },
      inviteCode: (value) => {
        return value && value.length === 8
          ? null
          : 'Please enter a valid invite code (Must be 8 characters)';
      },
    },
    validateInputOnChange: true,
  });

  const inviteCodeQueryOptions = trpc.auth.validateInviteCode.queryOptions(
    {
      inviteCode: form.values.inviteCode.trim().toUpperCase(),
    },
    {
      enabled: !!form.values.inviteCode && form.values.inviteCode.length >= 8,
      retry: 1, // Ensure we retry fewer times for invalid codes
      staleTime: 0, // Ensure we get a quick response
    },
  );

  // Define query options for invite code validation with enabled condition
  const { data: inviteCodeData, isLoading: isValidatingCode } = useQuery(inviteCodeQueryOptions);
  const emailDisabledByInvite = Boolean(
    form.values.inviteCode && !(inviteCodeData?.valid && inviteCodeData.profileType === 'admin'),
  );

  // Effect to handle invite code validation results
  useEffect(() => {
    // Only proceed if we have a response from the validation query
    if (inviteCodeData === undefined) return;

    if (inviteCodeData.valid && inviteCodeData.email) {
      // Valid invite code - ensure modal is closed
      setInviteCodeModalOpen(false);

      // Pre-fill email if not already set
      if (!form.values.email) {
        form.setFieldValue('email', inviteCodeData.email);
      }
    } else {
      // Invalid invite code - show modal
      logger.log(`Invalid invite code detected: ${form.values.inviteCode}`);
      form.setFieldValue('inviteCode', '');
      form.setFieldError('inviteCode', 'Invalid invite code');
      setManualInviteCode(''); // Reset the manual input field too
      setInviteCodeModalOpen(true);
    }
  }, [inviteCodeData, form]);

  // Initial setup when component mounts
  useEffect(() => {
    // Only run this once on mount
    if (inviteCodeFromUrl) {
      // If invite code is in URL, set it in the form
      form.setFieldValue('inviteCode', inviteCodeFromUrl);

      // For short invite codes, show modal immediately without waiting for validation
      if (inviteCodeFromUrl.length < 8) {
        form.setFieldError('inviteCode', 'Invalid invite code');
        setInviteCodeModalOpen(true);
      }
    } else {
      // No invite code in URL, show modal
      setInviteCodeModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on mount

  // Handle manual invite code submission
  const handleInviteCodeSubmit = () => {
    const normalizedInviteCode = manualInviteCode.trim().toUpperCase();

    if (normalizedInviteCode.length === 8) {
      // Close the modal first to prevent re-renders while navigating
      setInviteCodeModalOpen(false);

      // Navigate to the signup page with the invite code
      // This will cause a clean reload with the new invite code
      if (window.location.pathname === '/signup') {
        // If we're already on signup, update the URL via router navigation (replace history entry)
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('inviteCode', normalizedInviteCode);
        void navigate(`${newUrl.pathname}${newUrl.search}`, { replace: true });
        // Set the form value after URL update
        form.setFieldValue('inviteCode', normalizedInviteCode);
      } else {
        // If we're on another page, navigate to signup
        void navigate('/signup?inviteCode=' + encodeURIComponent(normalizedInviteCode));
      }
    }
  };

  const registerMutation = useMutation(
    trpc.auth.register.mutationOptions({
      onSuccess: (data) => {
        storeAuthTokens(
          {
            token: data.token,
            ...defined({ refreshToken: data.refreshToken }),
          },
          'signup-success',
        );
        setUser(data.user);
        setIsLoading(false);
        void navigate('/dashboard');
      },
      onError: (error) => {
        const signupErrorCode = getSignupErrorCode(error.data);
        const presentation = getSignupErrorPresentation({
          message: error.message || 'Signup failed. Please try again.',
          ...defined({ errorCode: error.data?.code, signupErrorCode }),
        });
        setSignupErrorMessage(presentation.formError ?? null);
        form.setFieldError('email', presentation.emailError);
        form.setFieldError('inviteCode', presentation.inviteCodeError);
        form.setFieldError('passwords.first', undefined);
        form.setFieldError('passwords.second', undefined);
        setIsLoading(false);
      },
    }),
  );

  const handleSubmit = async (values: typeof form.values) => {
    // Check if invite code is present and valid
    if (!values.inviteCode) {
      setInviteCodeModalOpen(true);
      return;
    }

    // Check for invalid codes
    if (values.inviteCode.length < 8 || !inviteCodeData?.valid) {
      form.setFieldError('inviteCode', 'Invalid invite code');
      setInviteCodeModalOpen(true);
      return;
    }

    setIsLoading(true);
    setSignupErrorMessage(null);
    form.setFieldError('email', undefined);
    form.setFieldError('inviteCode', undefined);

    try {
      registerMutation.mutate({
        ...values,
        phone: values.phone || null, // Only send phone if it's not empty
        password: values.passwords.first,
      });
      // Success case is handled by AuthContext which will redirect via the routing logic
    } catch (error) {
      logger.error({ error }, 'Registration failed:');
      form.setFieldError('email', 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setInviteCodeModalOpen(false);
    void navigate('/login');
  };

  return (
    <>
      {/* Invite Code Modal */}
      <Modal
        opened={inviteCodeModalOpen}
        onClose={handleModalClose}
        title={form.values.inviteCode ? 'Invalid Invite Code' : 'Invite Code Required'}
        closeOnClickOutside={true}
        closeOnEscape={true}
        withCloseButton={true}
      >
        <Stack>
          <Text size="sm">
            {form.values.inviteCode
              ? 'The invite code you entered is not valid. Please enter a valid invite code to continue.'
              : 'An invite code is required to sign up. Please enter your invite code below or use a sign-up link that includes an invite code.'}
          </Text>
          <TextInput
            required
            label="Invite Code"
            placeholder="Enter your invite code"
            value={manualInviteCode}
            onChange={(e) => setManualInviteCode(e.target.value.trim().toUpperCase())}
            error={
              manualInviteCode && manualInviteCode.length < 8
                ? 'Invite code must be at least 8 characters'
                : null
            }
            rightSection={
              isValidatingCode && (
                <Text size="xs" c="dimmed">
                  Validating...
                </Text>
              )
            }
          />
          <AppButton
            onClick={handleInviteCodeSubmit}
            disabled={!manualInviteCode || manualInviteCode.length < 8}
            loading={isLoading || isValidatingCode}
            variant="outline"
            intent="primary"
          >
            Continue
          </AppButton>
        </Stack>
      </Modal>

      <Container size="xs" py="xl">
        <Paper px="xl" py="sm" withBorder>
          <img
            src="/logos/logo-dark.png"
            alt="TEO"
            style={{
              width: '50%',
              maxWidth: '100px',
              display: 'block',
              margin: '0 auto',
            }}
          />
          <Title order={1} ta="center" mb="md">
            Sign Up
          </Title>
          <Text size="sm" ta="center" mb="xl">
            Create your account to get started
            <br />
            {form.values.inviteCode && 'Using invite code: ' + form.values.inviteCode}
          </Text>

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              {signupErrorMessage ? (
                <Alert color="red" title="Unable to complete signup" data-testid="signup-error">
                  {signupErrorMessage}
                </Alert>
              ) : null}

              <TextInput
                required
                label="First Name"
                placeholder="Enter your first name"
                aria-required="true"
                {...form.getInputProps('firstName')}
              />

              <TextInput
                required
                label="Last Name"
                placeholder="Enter your last name"
                aria-required="true"
                {...form.getInputProps('lastName')}
              />

              <TextInput
                required
                type="email"
                label="Email"
                placeholder="Enter your email address"
                disabled={emailDisabledByInvite}
                aria-required="true"
                {...form.getInputProps('email')}
              />

              <Tooltip
                label="Password must be at least 12 characters and contain only printable characters"
                position="top-start"
                withArrow
              >
                <PasswordInput
                  required
                  label="Password"
                  placeholder="Enter your password"
                  aria-required="true"
                  {...form.getInputProps('passwords.first')}
                />
              </Tooltip>

              <PasswordInput
                required
                label="Confirm Password"
                placeholder="Confirm your password"
                aria-required="true"
                {...form.getInputProps('passwords.second')}
              />

              <TextInput
                label="Phone Number (optional)"
                placeholder="Enter your phone number"
                {...form.getInputProps('phone')}
              />

              <Text size="sm">By signing up, you agree to our: </Text>
              <div>
                <Checkbox
                  required
                  label={
                    <Text component="span" size="sm">
                      <Anchor component="button" onClick={() => setTermsModalOpen(true)}>
                        Terms of Use
                      </Anchor>
                    </Text>
                  }
                  key={form.key('termsAccepted')}
                  {...form.getInputProps('termsAccepted', { type: 'checkbox' })}
                />
              </div>

              <div>
                <Checkbox
                  required
                  label={
                    <Text component="span" size="sm">
                      <Anchor component="button" onClick={() => setPrivacyModalOpen(true)}>
                        Privacy Policy
                      </Anchor>
                    </Text>
                  }
                  key={form.key('privacyAccepted')}
                  {...form.getInputProps('privacyAccepted', { type: 'checkbox' })}
                />
              </div>

              <AppButton
                type="submit"
                loading={isLoading}
                fullWidth
                disabled={
                  !form.values.termsAccepted ||
                  !form.values.privacyAccepted ||
                  !form.values.firstName ||
                  !form.values.lastName ||
                  !form.values.email ||
                  !form.values.passwords.first ||
                  !form.values.passwords.second
                }
                variant="outline"
                intent="primary"
                data-testid="signup-submit"
              >
                Sign Up
              </AppButton>

              <Divider label="Or" labelPosition="center" my="md" />
              <div style={{ marginTop: 'calc(-1 * var(--mantine-spacing-md))' }}>
                <GoogleAuthButton
                  mode="signup"
                  fullWidth
                  onSuccess={async () => {
                    await navigate('/dashboard');
                  }}
                  onError={(error) => {
                    logger.error({ error }, 'Google signup error:');
                    const presentation = getSignupErrorPresentation(error);
                    setSignupErrorMessage(presentation.formError ?? null);
                    form.setFieldError('email', presentation.emailError);
                    form.setFieldError('inviteCode', presentation.inviteCodeError);
                  }}
                />
              </div>
            </Stack>
          </form>
          <Text ta="center" size="sm" pt="xl">
            Already have an account? <Anchor href="/login">Sign in</Anchor>
          </Text>
        </Paper>
      </Container>

      {/* Modals */}
      <Modal
        opened={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title="Terms of Use"
        size="95%"
        styles={{
          content: { height: 'calc(100vh - 40px)' },
          body: { padding: '20px' },
        }}
      >
        <Stack>
          <div className="page-container" dangerouslySetInnerHTML={{ __html: termsHtml }} />
          <Group justify="flex-end" gap="sm" p="md">
            <AppButton
              variant="outline"
              intent="danger"
              onClick={() => {
                form.setFieldValue('termsAccepted', false);
                setTermsModalOpen(false);
              }}
            >
              Reject
            </AppButton>
            <AppButton
              variant="filled"
              intent="primary"
              onClick={() => {
                form.setFieldValue('termsAccepted', true);
                setTermsModalOpen(false);
              }}
            >
              Accept
            </AppButton>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        title="Privacy Policy"
        size="95%"
        styles={{
          content: { height: 'calc(100vh - 40px)' },
          body: { padding: '20px' },
        }}
      >
        <Stack>
          <div className="page-container" dangerouslySetInnerHTML={{ __html: privacyHtml }} />
          <Group justify="flex-end" gap="sm" p="md">
            <AppButton
              variant="outline"
              intent="danger"
              onClick={() => {
                logger.log('Reject pressed');
                form.setFieldValue('privacyAccepted', false);
                setPrivacyModalOpen(false);
              }}
            >
              Reject
            </AppButton>
            <AppButton
              variant="filled"
              intent="primary"
              onClick={() => {
                logger.log('Accept pressed');
                form.setFieldValue('privacyAccepted', true);
                logger.debug(
                  {
                    privacyAccepted: form.getValues().privacyAccepted,
                  },
                  'Privacy acceptance updated',
                );
                setPrivacyModalOpen(false);
              }}
            >
              Accept
            </AppButton>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
